import React, { useState, useEffect } from 'react';
import { db } from '../db';
import { getApiBaseUrl } from '../api-helper';
import axios from 'axios';
import { ReactComponent as InfoIcon } from '../images/icons/info.svg';

const CosyflowEditor = () => {
  // State management
  const [cosyflows, setCosyflows] = useState([]);
  const [selectedCosyflowId, setSelectedCosyflowId] = useState(null);
  const [editedCosyflow, setEditedCosyflow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [exampleImages, setExampleImages] = useState([]);

  // Load all cosyflows from IndexedDB on component mount
  useEffect(() => {
    loadCosyflows();
  }, []);

  const loadCosyflows = async () => {
    try {
      setLoading(true);
      const allCosyflows = await db.cosyflows.toArray();
      console.log('[CosyflowEditor] Loaded cosyflows from DB:', allCosyflows.length);
      setCosyflows(allCosyflows);
    } catch (error) {
      console.error('[CosyflowEditor] Error loading cosyflows:', error);
      setMessage({ type: 'error', text: 'Failed to load cosyflows' });
    } finally {
      setLoading(false);
    }
  };

  // Handle cosyflow selection
  const handleSelectCosyflow = (cosyflowId) => {
    const selected = cosyflows.find(c => c.id === cosyflowId);
    if (selected) {
      // Deep clone to avoid mutating original data
      const cloned = JSON.parse(JSON.stringify(selected));
      setEditedCosyflow(cloned);
      setSelectedCosyflowId(cosyflowId);
      setMessage(null);

      // Load existing example images if any
      const existingImages = cloned.data?.metadata?.example_urls || [];
      setExampleImages(existingImages);

      console.log('[CosyflowEditor] Selected cosyflow:', cloned.data.title);
    }
  };

  // Handle name change
  const handleNameChange = (newName) => {
    if (editedCosyflow) {
      setEditedCosyflow({
        ...editedCosyflow,
        data: {
          ...editedCosyflow.data,
          title: newName
        }
      });
    }
  };

  // Handle field importance change
  const handleImportanceChange = (fieldKey, newImportance) => {
    if (!editedCosyflow) return;

    const field = editedCosyflow.data.variables?.[fieldKey];

    // Prevent changing importance of truly required fields
    if (field?.required === true && field?.importance === 5) {
      setMessage({
        type: 'warning',
        text: `Cannot modify importance of required field: ${field.title || fieldKey}`
      });
      return;
    }

    setEditedCosyflow({
      ...editedCosyflow,
      data: {
        ...editedCosyflow.data,
        variables: {
          ...editedCosyflow.data.variables,
          [fieldKey]: {
            ...editedCosyflow.data.variables[fieldKey],
            importance: parseInt(newImportance)
          }
        }
      }
    });

    setMessage(null);
  };

  // Handle default value change
  const handleDefaultChange = (fieldKey, newDefault) => {
    if (!editedCosyflow) return;

    setEditedCosyflow({
      ...editedCosyflow,
      data: {
        ...editedCosyflow.data,
        variables: {
          ...editedCosyflow.data.variables,
          [fieldKey]: {
            ...editedCosyflow.data.variables[fieldKey],
            default: newDefault
          }
        }
      }
    });
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    try {
      const imagePromises = files.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = (e) => {
            resolve(e.target.result);
          };

          reader.onerror = (error) => {
            console.error('[CosyflowEditor] Error reading file:', error);
            reject(error);
          };

          reader.readAsDataURL(file);
        });
      });

      const base64Images = await Promise.all(imagePromises);

      // Add new images to the list
      setExampleImages(prev => [...prev, ...base64Images]);

      setMessage({
        type: 'success',
        text: `Added ${files.length} image${files.length > 1 ? 's' : ''} successfully!`
      });
    } catch (error) {
      console.error('[CosyflowEditor] Error uploading images:', error);
      setMessage({
        type: 'error',
        text: 'Failed to upload images. Please try again.'
      });
    }
  };

  // Handle image removal
  const handleRemoveImage = (index) => {
    setExampleImages(prev => prev.filter((_, i) => i !== index));
    setMessage({
      type: 'success',
      text: 'Image removed successfully!'
    });
  };

  // Submit new cosyflow to API
  const handleSubmit = async () => {
    if (!editedCosyflow) {
      setMessage({ type: 'error', text: 'No cosyflow selected' });
      return;
    }

    if (!editedCosyflow.data.title || editedCosyflow.data.title.trim() === '') {
      setMessage({ type: 'error', text: 'Cosyflow name cannot be empty' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: 'info', text: 'Saving new cosyflow...' });

      const apiBase = await getApiBaseUrl();
      const authToken = sessionStorage.getItem('api_token') || process.env.REACT_APP_PUBLIC_TOKEN;

      // Update metadata with example images
      const updatedMetadata = {
        ...editedCosyflow.data.metadata,
        example_urls: exampleImages
      };

      // Update the cosyflow data with new metadata
      editedCosyflow.data.metadata = updatedMetadata;

      // Prepare the payload
      const payload = {
        title: editedCosyflow.data.title,
        cosyflow: JSON.stringify(editedCosyflow.data),
        category: editedCosyflow.data.category || 'Custom',
        description: editedCosyflow.data.description || '',
        metadata: updatedMetadata
      };

      console.log('[CosyflowEditor] Submitting new cosyflow:', payload.title);

      // POST to API endpoint
      const response = await axios.post(
        `${apiBase}/api/cosyflows/`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      console.log('[CosyflowEditor] Response:', response.data);

      // Check for existing cosyflow with same title to prevent duplicates
      const existingCosyflow = await db.cosyflows
        .where('data.title')
        .equals(editedCosyflow.data.title)
        .first();

      if (existingCosyflow) {
        // Update existing record instead of creating duplicate
        await db.cosyflows.update(existingCosyflow.id, {
          data: editedCosyflow.data,
          timestamp: Date.now()
        });
        console.log('[CosyflowEditor] Updated existing cosyflow with ID:', existingCosyflow.id);
      } else {
        // Create new record only if title doesn't exist
        const newId = await db.cosyflows.add({
          data: editedCosyflow.data,
          timestamp: Date.now()
        });
        console.log('[CosyflowEditor] Created new cosyflow with ID:', newId);
      }

      setMessage({
        type: 'success',
        text: existingCosyflow
          ? `Successfully updated cosyflow: "${editedCosyflow.data.title}"`
          : `Successfully created new cosyflow: "${editedCosyflow.data.title}"`
      });

      // Reload cosyflows and reset selection
      await loadCosyflows();
      setEditedCosyflow(null);
      setSelectedCosyflowId(null);
      setExampleImages([]);
    } catch (error) {
      console.error('[CosyflowEditor] Error saving cosyflow:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save cosyflow';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  // Filter cosyflows by search query
  const filteredCosyflows = cosyflows.filter(c =>
    c.data?.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render field editor
  const renderFieldEditor = (fieldKey, fieldData) => {
    const isRequired = fieldData.required === true && fieldData.importance === 5;
    const currentImportance = fieldData.importance || 4;

    return (
      <div
        key={fieldKey}
        className="bg-dark-blue/40 p-6 rounded-xl border-2 border-white/30 mb-6 hover:border-cyan/50 transition-all"
      >
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <h4 className="text-white font-bold text-xl">
              {fieldData.title || fieldKey}
            </h4>
            {isRequired && (
              <span className="bg-red-500/30 text-red-300 px-4 py-2 rounded-lg text-xs font-bold border-2 border-red-500/50">
                🔒 REQUIRED - LOCKED
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <p className="text-white/70 text-base">
              Type: <span className="text-cyan font-mono font-bold text-lg">{fieldData.type}</span>
            </p>
            {fieldData.description && (
              <p className="text-white/80 text-sm italic">{fieldData.description}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {/* Importance Slider - Full Width, Bigger */}
          <div className="bg-dark-purple/30 p-6 rounded-xl border border-white/20">
            <label className="block mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-yellow font-bold text-lg">Field Visibility</span>
                <div className="flex items-center gap-3">
                  <span className="text-white bg-yellow/30 px-6 py-3 rounded-lg font-bold text-xl border-2 border-yellow/50">
                    {currentImportance}
                  </span>
                  {isRequired && (
                    <span className="text-red-400 text-sm font-semibold">(Cannot Modify)</span>
                  )}
                </div>
              </div>
            </label>
            <input
              type="range"
              min="1"
              max="5"
              step="1"
              value={currentImportance}
              onChange={(e) => handleImportanceChange(fieldKey, e.target.value)}
              disabled={isRequired}
              className={`w-full h-3 ${isRequired ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{
                accentColor: '#00D9FF'
              }}
            />
            <div className="flex justify-between mt-4 px-1">
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">1</div>
                <div className="text-white/60 text-sm">Hidden</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">2</div>
                <div className="text-white/60 text-sm">Low</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">3</div>
                <div className="text-white/60 text-sm">Optional</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">4</div>
                <div className="text-white/60 text-sm">High</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">5</div>
                <div className="text-white/60 text-sm">Always</div>
              </div>
            </div>
          </div>

          {/* Default Value - Full Width, Bigger */}
          <div className="bg-dark-purple/30 p-6 rounded-xl border border-white/20">
            <label className="block text-yellow font-bold text-lg mb-4">
              Default Value
            </label>
            {fieldData.type === 'BOOLEAN' || fieldData.type === 'boolean' || fieldData.type === 'BOOL' ? (
              <select
                value={fieldData.default === true || fieldData.default === 'true' ? 'true' : 'false'}
                onChange={(e) => handleDefaultChange(fieldKey, e.target.value === 'true')}
                className="w-full p-4 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-lg font-semibold focus:border-cyan focus:outline-none"
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : fieldData.type === 'enum' || fieldData.type === 'COMBO' ? (
              <select
                value={fieldData.default || ''}
                onChange={(e) => handleDefaultChange(fieldKey, e.target.value)}
                className="w-full p-4 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-lg font-semibold focus:border-cyan focus:outline-none"
              >
                {fieldData.options?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : fieldData.type === 'FLOAT' || fieldData.type === 'INT' || fieldData.type === 'number' ? (
              <input
                type="number"
                value={fieldData.default ?? ''}
                onChange={(e) => handleDefaultChange(fieldKey, parseFloat(e.target.value))}
                min={fieldData.min}
                max={fieldData.max}
                step={fieldData.step || (fieldData.type === 'FLOAT' ? 0.1 : 1)}
                className="w-full p-4 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-lg font-semibold focus:border-cyan focus:outline-none"
              />
            ) : (
              <textarea
                value={fieldData.default ?? ''}
                onChange={(e) => handleDefaultChange(fieldKey, e.target.value)}
                placeholder="Enter default value..."
                rows="3"
                className="w-full p-4 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base placeholder-white/40 focus:border-cyan focus:outline-none resize-y"
              />
            )}
            {fieldData.min !== undefined && fieldData.max !== undefined && (
              <p className="text-sm text-white/70 mt-3 font-medium">
                📊 Range: {fieldData.min} to {fieldData.max}
                {fieldData.step && ` • Step: ${fieldData.step}`}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-[1800px]">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-white mb-4">🎨 CosyFlow Editor</h1>
          <p className="text-white/80 text-lg font-medium">
            Clone and customize existing workflows by adjusting field visibility and defaults
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`mb-8 p-6 rounded-xl border-2 text-base font-semibold ${
              message.type === 'error' ? 'bg-red-500/20 border-red-500/50 text-red-200' :
              message.type === 'success' ? 'bg-green-500/20 border-green-500/50 text-green-200' :
              message.type === 'warning' ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-200' :
              'bg-cyan/20 border-cyan/50 text-cyan'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Selector Panel */}
          <div className="lg:col-span-1">
            <div className="bg-dark-blue/40 rounded-2xl p-8 border-2 border-white/30 sticky top-6">
              <h2 className="text-2xl font-bold text-white mb-6">📋 Select Workflow</h2>

              {/* Search */}
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 Search workflows..."
                className="w-full mb-6 p-5 rounded-xl bg-dark-purple/60 border-2 border-white/30 text-white text-base placeholder-white/50 focus:border-cyan focus:outline-none"
              />

              {/* Loading State */}
              {loading && (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-yellow"></div>
                  <p className="text-white/70 mt-4 text-base font-medium">Loading workflows...</p>
                </div>
              )}

              {/* Cosyflow List */}
              {!loading && (
                <div className="space-y-3 max-h-[700px] overflow-y-auto pr-2">
                  {filteredCosyflows.length === 0 ? (
                    <p className="text-white/60 text-center py-12 text-base">
                      {searchQuery ? 'No matching workflows found' : 'No workflows available'}
                    </p>
                  ) : (
                    filteredCosyflows.map(cosyflow => (
                      <button
                        key={cosyflow.id}
                        onClick={() => handleSelectCosyflow(cosyflow.id)}
                        className={`w-full text-left p-5 rounded-xl transition-all border-2 ${
                          selectedCosyflowId === cosyflow.id
                            ? 'bg-yellow text-dark-blue font-bold border-yellow shadow-lg scale-105'
                            : 'bg-dark-purple/50 text-white hover:bg-dark-purple/70 border-white/20 hover:border-cyan/50'
                        }`}
                      >
                        <div className="font-semibold text-base truncate mb-1">
                          {cosyflow.data?.title || 'Untitled'}
                        </div>
                        {cosyflow.data?.category && (
                          <div className="text-sm opacity-80">
                            📁 {cosyflow.data.category}
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Editor Panel */}
          <div className="lg:col-span-3">
            {!editedCosyflow ? (
              <div className="bg-dark-blue/40 rounded-2xl p-20 border-2 border-white/30 text-center">
                <div className="mb-8">
                  <svg className="w-32 h-32 mx-auto text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">No Workflow Selected</h3>
                <p className="text-white/70 text-lg">
                  👈 Select a workflow from the list to start customizing
                </p>
              </div>
            ) : (
              <div className="bg-dark-blue/40 rounded-2xl p-8 border-2 border-white/30">
                {/* Name Editor */}
                <div className="mb-10 pb-8 border-b-2 border-white/30">
                  <label className="block text-yellow font-bold text-xl mb-4">
                    ✏️ Workflow Name *
                  </label>
                  <input
                    type="text"
                    value={editedCosyflow.data.title || ''}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Enter workflow name..."
                    className="w-full p-6 rounded-xl bg-dark-purple/60 border-2 border-white/30 text-white text-xl font-bold placeholder-white/40 focus:border-cyan focus:outline-none"
                  />
                  <p className="text-white/60 text-base mt-3 font-medium">
                    💡 Give your customized workflow a unique, descriptive name
                  </p>
                </div>

                {/* Example Images Upload Section */}
                <div className="mb-10 pb-8 border-b-2 border-white/30">
                  <div className="mb-6">
                    <label className="block text-yellow font-bold text-xl mb-2">
                      🖼️ Example Images
                    </label>
                    <p className="text-white/60 text-base font-medium">
                      Upload preview images to showcase your workflow in the gallery
                    </p>
                  </div>

                  {/* Upload Button */}
                  <div className="mb-6">
                    <label className="cursor-pointer inline-block">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <div className="bg-cyan/20 hover:bg-cyan/30 border-2 border-cyan/50 hover:border-cyan text-white font-bold py-5 px-8 rounded-xl transition-all inline-flex items-center gap-3 text-lg">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Upload Images
                      </div>
                    </label>
                    <p className="text-white/50 text-sm mt-3">
                      You can select multiple images at once. Supported formats: JPG, PNG, GIF, WebP
                    </p>
                  </div>

                  {/* Image Preview Grid */}
                  {exampleImages.length > 0 && (
                    <div>
                      <h4 className="text-white font-bold text-lg mb-4">
                        Uploaded Images ({exampleImages.length})
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {exampleImages.map((image, index) => (
                          <div
                            key={index}
                            className="relative group bg-dark-purple/40 rounded-xl overflow-hidden border-2 border-white/20 hover:border-cyan/50 transition-all"
                          >
                            <img
                              src={image}
                              alt={`Example ${index + 1}`}
                              className="w-full h-48 object-cover"
                            />
                            {/* Remove Button */}
                            <button
                              onClick={() => handleRemoveImage(index)}
                              className="absolute top-2 right-2 bg-red-500/90 hover:bg-red-600 text-white rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-all"
                              title="Remove image"
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            {/* Image Number Badge */}
                            <div className="absolute bottom-2 left-2 bg-black/70 text-white px-3 py-1 rounded-lg text-xs font-bold">
                              #{index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {exampleImages.length === 0 && (
                    <div className="bg-dark-purple/30 rounded-xl border-2 border-dashed border-white/20 p-12 text-center">
                      <svg className="w-20 h-20 mx-auto text-white/30 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-white/60 text-base font-medium">
                        No example images uploaded yet
                      </p>
                      <p className="text-white/40 text-sm mt-2">
                        Click "Upload Images" above to add preview images
                      </p>
                    </div>
                  )}
                </div>

                {/* Fields Editor */}
                <div className="mb-8">
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white mb-3">⚙️ Field Configuration</h3>
                    <p className="text-white/80 text-base">
                      Customize field visibility and default values. 🔒 Required fields cannot be modified.
                    </p>
                  </div>

                  {editedCosyflow.data.variables && Object.keys(editedCosyflow.data.variables).length > 0 ? (
                    <div className="space-y-6">
                      {Object.entries(editedCosyflow.data.variables).map(([key, value]) =>
                        renderFieldEditor(key, value)
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-16 text-white/60 bg-dark-purple/30 rounded-xl border-2 border-white/20">
                      <p className="text-xl font-semibold">No configurable fields found</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-6 pt-8 border-t-2 border-white/30">
                  <button
                    onClick={() => {
                      setEditedCosyflow(null);
                      setSelectedCosyflowId(null);
                      setExampleImages([]);
                      setMessage(null);
                    }}
                    disabled={saving}
                    className="flex-1 bg-dark-purple/60 hover:bg-dark-purple/80 text-white font-bold py-6 px-8 rounded-xl transition-all border-2 border-white/30 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                  >
                    ✖️ Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={saving || !editedCosyflow.data.title}
                    className="flex-1 bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-6 px-8 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg shadow-lg hover:shadow-xl"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-dark-blue"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <span>💾</span>
                        Save as New Workflow
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CosyflowEditor;